import React from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { SectionExpanded } from '@components/Profile/CarouselsExpanded';
import { ArtistHero } from '@components/Profile/ProfileHero/ArtistHero';
import { UserInfo } from '@components/Profile/Sections/UserInfo';
import { ProfileContainer, ProfileWrapper } from '@components/Profile/shared';
import type { SeoProps } from '@components/Seo';
import { Seo } from '@components/Seo';
import type { ArtistProfileNewQuery } from '@graphql/reactQuery';
import {
  ArtistProfileNewDocument,
  fetchGQL,
  getFragment,
  useQuery,
  UserSoundCollectionFragmentDoc,
} from '@graphql/reactQuery';
import { getUserOpenGraphImage } from '@utils/helpers';

const ISR_REVALIDATE_PERIOD = 20;
export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { soundHandle } = params || {};

  if (!soundHandle || Array.isArray(soundHandle)) {
    return {
      redirect: {
        destination: '/404',
        permanent: true,
      },
    };
  }

  return Promise.all([
    fetchGQL(ArtistProfileNewDocument, {
      variables: { soundHandle },
    }),
  ]).then(async ([data]) => {
    const artist = data.data.artistByHandle;
    const user = data.data.userByArtistHandle;
    if (!artist || !user) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        revalidate: ISR_REVALIDATE_PERIOD,
      };
    }

    const userOg = getUserOpenGraphImage({ userId: user.id });
    /**
     * If there is no OG image and there is no banner photo then we show profile photo
     * If we show profile photo it needs to be small card
     */
    const smallTwitterCard = !userOg && !artist.bannerImage;

    const props: PageProps = {
      seo: {
        title: artist.name || null,
        twitterHandle: user.twitterHandle || null,
        description: user.description || null,
        image: userOg || user.avatar?.url || artist.bannerImage?.url || null,
        smallTwitterCard,
        audioUrl: null,
        url: artist.webappUri,
      },
      soundHandle,
      artist,
      user,
    };

    return {
      props,
      revalidate: ISR_REVALIDATE_PERIOD,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

type PageProps = {
  seo: SeoProps;
  soundHandle: string;
  artist: NonNullable<ArtistProfileNewQuery['artistByHandle']>;
  user: NonNullable<ArtistProfileNewQuery['userByArtistHandle']>;
};

type SharedArtistProfilePropsType = {
  children?: React.ReactNode;
} & PageProps;

export default function ArtistProfilePlaylists({
  soundHandle,
  seo,
  artist: artistProp,
  user: userProp,
}: SharedArtistProfilePropsType) {
  const { data } = useQuery(ArtistProfileNewDocument, {
    variables: { soundHandle },
    initialData: {
      data: {
        artistByHandle: artistProp,
        userByArtistHandle: userProp,
      },
    },
    staleTime: 0,
  });

  const user = data?.data.userByArtistHandle || userProp;
  const artistUserFragment = getFragment(UserSoundCollectionFragmentDoc, user);
  const artist = data?.data.artistByHandle || artistProp;

  return (
    <>
      <Seo {...seo} />
      <ProfileContainer>
        <ArtistHero artistData={artist} />
        <ProfileWrapper>
          <UserInfo userData={artistUserFragment} />
          <SectionExpanded profileSection="playlists" user={user} artist={artistProp} />
        </ProfileWrapper>
      </ProfileContainer>
    </>
  );
}
